"use client"

import { useEffect, useMemo } from "react"
import { Groups, MatchedStatuses, PriorityData, ProjectPage, pageview } from "utils"
import { RelatedProjects } from "utils/utils"
import ApartmentMarketingForRent from "./Marketing/ApartmentMarketingForRent/ApartmentMarketingForRent"
import { FilterProvider } from "./Marketing/FilterContext"
import Marketing from "./Marketing/Marketing"
import { createDefaultState } from "./Marketing/PropertyTab"
import ProjectLayoutManager from "./ProjectLayoutManager"
import StandardProject from "./StandardProject"

export type ProjectsMessages = {
  contactUs: string
  priceOffer: string
  forYou: string
  required: string
  subject: string
  fullName: string
  newFullName?: string
  telephone: string
  email: string
  newEmail?: string
  messageOptional: string
  consentMessage: string
  privacy: string
  terms: string
  send: string
  groups: string
  collaborators: string
  entr: string
  leaveDetails: string
  close: string
  virtualTour: string
  sales: string
  salesCenter: string
  callNow?: string
  toProject: string
  sucessTitle: string
  sucessText: string
  contactSucessTitle: string
  contactSucessText: string
  rentforyou?: string
  successTitleRent?: string
  successTextRent?: string
  idNumber?: string
}

type Props = {
  project: ProjectPage
  relatedProjects?: RelatedProjects
  statuses: MatchedStatuses
  groups: Groups
  formSubjects: Record<string, string>
  designations: string[]
  cities: string[]
  countries: string[]
  locale: string
  messages: ProjectsMessages
  propertyTypes: { id: number; value: string }[]
  priorityData?: PriorityData
  companyName?: string
  subcompany?: string
  toLobby?: string
  narkisCondensedVariable?: string
  narkisVariable?: string
}

function Projects({
  locale,
  project,
  statuses,
  groups,
  formSubjects,
  designations,
  cities,
  countries,
  messages,
  propertyTypes,
  relatedProjects,
  companyName,
  subcompany,
  priorityData,
  toLobby,
  narkisVariable,
  narkisCondensedVariable,
}: Props) {
  const { projectType } = project

  // const isLegacy = projectType === "מורשת"
  // const isPlanning = projectType === "תכנון ובנייה"
  const isPropertyMarketing = projectType === "שיווק נכסים"
  const isApartmentMarketing = projectType === "שיווק דירות"
  const entitledProject = projectType === "זכאים"
  const isApartmentMarketingForRent = projectType === "שיווק דירות - להשכרה"

  const is4Rent = companyName === "Ashtrom Residential for rent"

  useEffect(() => {
    pageview(companyName || "", subcompany || "", projectType || "", locale)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const defaultState = useMemo(
    () =>
      createDefaultState({
        apartmProp: priorityData?.allApChar ?? [],
        buildProp: priorityData?.allBuChar ?? [],
      }),
    [priorityData?.allApChar, priorityData?.allBuChar],
  )
  const renderProjectContent = () => {
    if (isApartmentMarketingForRent) {
      return (
        <ApartmentMarketingForRent
          toLobby={toLobby}
          cities={cities}
          countries={countries}
          designations={designations}
          formSubjects={formSubjects}
          groups={groups}
          locale={locale}
          statuses={statuses}
          priorityData={priorityData}
          projectData={project}
          messages={messages}
          relatedProjects={relatedProjects}
          propertyTypes={propertyTypes}
          narkisVariable={narkisVariable}
          narkisCondensedVariable={narkisCondensedVariable}
        />
      )
    } else if (isApartmentMarketing || isPropertyMarketing || entitledProject) {
      return (
        <Marketing
          toLobby={toLobby}
          cities={cities}
          countries={countries}
          designations={designations}
          formSubjects={formSubjects}
          groups={groups}
          locale={locale}
          statuses={statuses}
          priorityData={priorityData}
          projectData={project}
          messages={messages}
          relatedProjects={!is4Rent ? relatedProjects : undefined}
          propertyTypes={propertyTypes}
        />
      )
    } else {
      return (
        <StandardProject
          toLobby={toLobby}
          cities={cities}
          countries={countries}
          designations={designations}
          formSubjects={formSubjects}
          groups={groups}
          locale={locale}
          messages={messages}
          project={project}
          propertyTypes={propertyTypes}
          statuses={statuses}
        />
      )
    }
  }

  // Manage header/footer visibility based on project type
  return (
    <ProjectLayoutManager hideHeaderFooter={isApartmentMarketingForRent}>
      <FilterProvider defaultState={defaultState}>{renderProjectContent()}</FilterProvider>
    </ProjectLayoutManager>
  )
}

export default Projects
